import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import DayList from '../../../daysList/DayList'
import { Tabs } from '../../../tabs/Tabs'
import Loading from '../../../loading/Loading'
import CourtStartGrid, { GridStart } from './courtStartGrid/CourtStartGrid'

import { useConfig } from '../../../../contexts/ConfigContext'
import { MANAGEMENT_DAYS_AHEAD } from '../../../../constants/booking'
import { SessionPicker as SessionPickerState } from '../../../../hooks/booking/useSessionPicker'
import { AvailableStart, CourtAvailability, OccupiedType } from '../../../../types/booking/booking'
import { formatDate, getDays } from '../../../../utils/dateUtils'
import { formatMinutes, overlaps } from '../../../../utils/booking/booking'

const DEFAULT_BOOKING_WINDOW_DAYS = 14

const toMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

/** Whether a club-local date-time falls inside [startTime, endTime). */
const contains = (interval: { startTime: string; endTime: string }, time: string) =>
  interval.startTime.slice(0, 16) <= time.slice(0, 16) && time.slice(0, 16) < interval.endTime.slice(0, 16)

const pad = (value: number) => String(value).padStart(2, '0')

const formatLocal = (day: Date) =>
  `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}T${pad(day.getHours())}:${pad(day.getMinutes())}:00`

const toLocalDateTime = (date: string, minuteOfDay: number) => {
  const day = new Date(`${date}T00:00:00`)
  day.setMinutes(minuteOfDay)
  return formatLocal(day)
}

/**
 * Every start on the club grid for the duration, marked available when the server offered it. A start not offered is
 * "Booked", "On hold" (a booking in checkout) or the block reason when the court is occupied at that time, and
 * "Overlaps" when it is free but the session would run into an occupied interval. Purely presentational: which starts are bookable is decided by the server.
 */
const buildGrid = (
  court: CourtAvailability,
  date: string,
  durationMinutes: number,
  openTime?: string,
  closeTime?: string,
  increment?: number
): GridStart[] => {
  const offered = new Map(court.starts.map(start => [start.startTime.slice(0, 16), start]))
  if (!openTime || !closeTime || !increment) {
    return court.starts.map(start => ({ ...start, available: true }))
  }
  const open = toMinutes(openTime)
  const closeRaw = toMinutes(closeTime)
  const close = closeRaw <= open ? closeRaw + 24 * 60 : closeRaw
  const now = formatLocal(new Date())
  const grid: GridStart[] = []
  for (let minute = open; minute + durationMinutes <= close; minute += increment) {
    const startTime = toLocalDateTime(date, minute)
    const start = offered.get(startTime.slice(0, 16))
    if (start) {
      grid.push({ ...start, available: true })
      continue
    }
    const endTime = toLocalDateTime(date, minute + durationMinutes)
    const session = { startTime, endTime }
    const occupiedAt = court.occupied.find(item => contains(item, startTime))
    // Past starts (and free ones the server does not offer for another reason) stay "Booked".
    const runsInto = !occupiedAt && startTime > now && court.occupied.some(item => overlaps(item, session))
    grid.push({
      ...session,
      available: false,
      blockReason: occupiedAt?.type === OccupiedType.BLOCK ? occupiedAt.reason : undefined,
      held: occupiedAt?.type === OccupiedType.HOLD,
      overlaps: runsInto,
    })
  }
  return grid
}

type SessionPickerProps = {
  picker: SessionPickerState
  showPrices?: boolean
  disabled?: boolean
  /** False when the page shows the day strip itself (see SessionPickerDays). */
  showDays?: boolean
  /** Administrative booking: any future day can be picked from a calendar. */
  anyDay?: boolean
  /** Starts the customer's prepaid minutes cover (labelled "Pre-paid"). */
  isIncluded?: (courtId: number, start: AvailableStart, durationMinutes: number) => boolean
}

/**
 * Club and session length, on one row above the booking columns (so the day picker lines up with the summary).
 */
export const SessionPickerSetup = ({ picker }: { picker: SessionPickerState }) => {
  const { t } = useTranslation('bookings')

  return (
    <div className="session-picker-setup">
      {picker.clubs.length > 1 && (
        <div className="setup-row">
          <span className="setup-label">{t('booking.session-picker.club')}</span>
          <Tabs
            tab={String(picker.clubId ?? '')}
            tabs={picker.clubs.map(club => ({ value: String(club.id), label: club.name }))}
            onChange={value => picker.setClubId(Number(value))}
          />
        </div>
      )}
      <div className="setup-row">
        <span className="setup-label">{t('booking.session-picker.duration')}</span>
        {picker.durations.length > 0 ? (
          <Tabs
            tab={String(picker.durationMinutes ?? '')}
            tabs={picker.durations.map(duration => ({
              value: String(duration),
              label: formatMinutes(duration),
            }))}
            onChange={value => picker.setDurationMinutes(Number(value))}
          />
        ) : (
          <span className="muted">{t('booking.session-picker.no-durations')}</span>
        )}
      </div>
    </div>
  )
}

/**
 * Days offered in the strip, from today: the user's booking window from the config (the server checks it again on
 * booking), or a fixed range for the management views, where the calendar card reaches any other day.
 */
const useBookingDays = (management: boolean) => {
  const { config } = useConfig()
  const daysAhead = management ? MANAGEMENT_DAYS_AHEAD : config?.bookingWindowDays ?? DEFAULT_BOOKING_WINDOW_DAYS
  return getDays(new Date(), daysAhead + 1)
}

type SessionPickerDaysProps = {
  picker: SessionPickerState
  /** Administrative booking: a calendar card before the strip picks any future day. */
  anyDay?: boolean
}

/** The day strip on its own, for layouts that show it full width above the courts and the summary. */
export const SessionPickerDays = ({ picker, anyDay = false }: SessionPickerDaysProps) => {
  const days = useBookingDays(anyDay)

  return (
    <DayList
      days={days}
      selectedDay={picker.day}
      setSelectedDay={picker.setDay}
      anyDay={anyDay ? { disablePastDates: true } : undefined}
    />
  )
}

const SessionPicker = ({
  picker,
  showPrices = true,
  disabled = false,
  showDays = true,
  anyDay = false,
  isIncluded,
}: SessionPickerProps) => {
  const { t } = useTranslation('bookings')
  const date = formatDate(picker.day) ?? ''

  const courts = useMemo(() => {
    if (!picker.availability || picker.durationMinutes === undefined) {
      return []
    }
    return picker.availability.courts.map(court => ({
      court,
      starts: buildGrid(
        court,
        date,
        picker.durationMinutes!,
        picker.club?.openTime,
        picker.club?.closeTime,
        picker.availability!.incrementMinutes
      ),
    }))
  }, [picker.availability, picker.durationMinutes, picker.club, date])

  return (
    <div className="session-picker">
      {showDays && <SessionPickerDays picker={picker} anyDay={anyDay} />}

      <div className={`court-stack${showDays ? '' : ' mt-0'}`}>
        {picker.availabilityLoading && !picker.availability ? (
          <Loading variant="inline" />
        ) : courts.length === 0 ? (
          <div className="empty muted">{t('booking.session-picker.no-courts')}</div>
        ) : (
          courts.map(({ court, starts }) => (
            <CourtStartGrid
              key={court.courtId}
              court={court}
              clubName={picker.club?.name}
              durationMinutes={picker.durationMinutes ?? 0}
              starts={starts}
              showPrices={showPrices}
              disabled={disabled}
              isSelected={start => picker.isSelected(court.courtId, start)}
              isCovered={start => picker.isCovered(court.courtId, start)}
              coveredSelectable={picker.single}
              isBlocked={start => picker.isBlocked(court.courtId, start)}
              isIncluded={
                isIncluded
                  ? start => isIncluded(court.courtId, start, picker.durationMinutes ?? 0)
                  : undefined
              }
              onToggle={start => picker.toggle(court, start)}
            />
          ))
        )}
      </div>
    </div>
  )
}

export default SessionPicker
